import { trapFocus, removeTrapFocus, getFocusableElements, scrollLock } from '@utilities/theme';

const drawerDebounce = (fn, wait) => {
  let timeoutId;

  return (...args) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => fn(...args), wait);
  };
};

export class SlideOutDrawer extends HTMLElement {
  constructor() {
    super();
    this.container = null;
    this.backdrop = null;
    this.closeButtons = null;
    this.isOpen = false;
    this.identifier = '';
    this.fullIdentifier = '';
    this.isInitialized = false;
    this.observer = null;
    this.escapeListener = null;
    this.focusedElementBeforeOpen = null;
    this.animationDuration = 300;
    this.alphabetLinks = null;
    this.visualFilters = null;
    this.isMoved = false;
  }

  connectedCallback() {
    if (this.isInitialized) return;

    if (!this.isMoved && this.parentElement !== document.body) {
      this.moveToBody();
      return;
    }

    this.isInitialized = true;
    this.initializeDrawer();
  }

  moveToBody = () => {
    this.isMoved = true;
    this.remove();
    document.body.appendChild(this);
  }

  initializeDrawer = () => {
    this.setElements();
    if (!this.hasElements()) return;
    this.setVariables();
    this.setEventListeners();
  }

  setElements = () => {
    this.container = this.querySelector('.slide-out-drawer__container');
    this.backdrop = this.querySelector('.slide-out-drawer__backdrop');
    this.closeButtons = this.querySelectorAll('.slide-out-drawer__close');
    this.alphabetLinks = this.querySelectorAll('.slide-out-drawer__alphabetical-navigation-item.has-filters button');
    this.visualFilters = this.querySelectorAll('.slide-out-drawer__content--visual-filter');
  }

  hasElements = () => {
    // Only the container and backdrop are required structural elements. Close
    // buttons may be injected later (e.g. FilterDrawer renders them from a
    // <template> after filters load, then wires them via setupCloseButtonListeners),
    // so their absence at init time is not a failure.
    if (!this.container || !this.backdrop) {
      console.error('Slide-out drawer: Required elements not found', {
        container: !!this.container,
        backdrop: !!this.backdrop,
      });
      return false;
    }
    return true;
  }

  setVariables = () => {
    this.isOpen = this.classList.contains('open');
    this.identifier = this.dataset.identifier;
    this.fullIdentifier = this.getAttribute('id');
  }

  setEventListeners = () => {
    this.addEventListener('facets:initialized', this.handleFacetsInitialized.bind(this));
    for (const button of this.closeButtons) {
      button.addEventListener('click', this.close.bind(this));
    }
    this.backdrop.addEventListener('click', this.close.bind(this));
    this.escapeListener = (event) => {
      if (event.key === 'Escape') {
        this.close();
      }
    };
    this.addEventListener('click', (event) => event.stopPropagation());
    this.initializeAlphabeticalNavigation();
  }

  initializeAlphabeticalNavigation() {
    if (this.alphabetLinks.length === 0) return;
    for (const link of this.alphabetLinks) {
      link.addEventListener('click', this.handleAlphabetClick.bind(this));
    }
  }

  handleFacetsInitialized(event) {
    if (!this.contains(event.target)) return;
    this.initializeDrawer();
  }

  handleAlphabetClick(event) {
    const letter = event.target.dataset.letter;
    if (!letter) return;
    const targetFilter = Array.from(this.visualFilters).find((filter) => filter.dataset.letter === letter);
    if (!targetFilter) return;

    const contentContainer = this.querySelector('.slide-out-drawer__content');
    if (!contentContainer) return;

    const targetPosition = targetFilter.offsetTop - contentContainer.offsetTop;
    contentContainer.scrollTo({
      top: targetPosition,
      behavior: 'smooth',
    });
  }

  disconnectedCallback = () => {
    if (!this.isMoved) {
      const lock = scrollLock;
      if (lock && typeof lock.disableBodyScrollLock === 'function') {
        lock.disableBodyScrollLock();
      }

      if (typeof removeTrapFocus === 'function') {
        removeTrapFocus();
      }

      document.removeEventListener('keydown', this.escapeListener);
      if (this.focusedElementBeforeOpen) {
        this.focusedElementBeforeOpen.focus();
      }
    }
    this.isInitialized = false;
  }

  open = () => {
    if (!this.isInitialized || this.isDrawerOpen()) return;

    this.focusedElementBeforeOpen = document.activeElement;
    const lock = scrollLock;
    if (lock && typeof lock.enabledBodyScrollLock === 'function') {
      lock.enabledBodyScrollLock();
    }

    document.addEventListener('keydown', this.escapeListener);

    requestAnimationFrame(() => {
      this.classList.add('open');
      this.setAttribute('aria-hidden', 'false');
      this.isOpen = true;
      document.body.classList.add('overflow-hidden');

      if (typeof trapFocus === 'function') {
        trapFocus(this.container);
      }

      if (typeof getFocusableElements === 'function') {
        const firstFocusableElement = getFocusableElements(this.container)[0];
        if (firstFocusableElement) {
          firstFocusableElement.focus();
        }
      }

      this.dispatchEvent(new CustomEvent('slide-out-drawer:open', { bubbles: true }));
    });
  }

  close = (event) => {
    if (event && event.preventDefault) {
      event.preventDefault();
    }
    if (!this.isInitialized || !this.isDrawerOpen()) return;

    const lock = scrollLock;
    if (lock && typeof lock.disableBodyScrollLock === 'function') {
      lock.disableBodyScrollLock();
    }

    document.removeEventListener('keydown', this.escapeListener);
    this.classList.add('closing');
    this.classList.remove('open');

    const handleAnimationEnd = () => {
      this.classList.remove('closing');
      this.setAttribute('aria-hidden', 'true');
      this.isOpen = false;

      if (typeof removeTrapFocus === 'function') {
        removeTrapFocus();
      }

      document.body.classList.remove('overflow-hidden');
      if (this.focusedElementBeforeOpen) {
        this.focusedElementBeforeOpen.focus();
      }
      this.dispatchEvent(new CustomEvent('slide-out-drawer:close', { bubbles: true }));
      this.removeEventListener('animationend', handleAnimationEnd);
    };

    this.addEventListener('animationend', handleAnimationEnd);
  }

  toggle = () => {
    this.isOpen ? this.close() : this.open();
  }

  isDrawerOpen = () => this.isOpen
}

if (!customElements.get('slide-out-drawer')) {
  customElements.define('slide-out-drawer', SlideOutDrawer);
}

export class SlideOutDrawerOpener extends HTMLElement {
  connectedCallback() {
    if (this.isInitialized) return;

    this.isInitialized = true;
    this.button = this.querySelector('button');
    if (!this.button) {
      console.error('Slide-out drawer opener: Button not found');
      return;
    }

    this.drawerId = this.dataset.drawer || 'slide-out-drawer-null';
    this.drawer = document.querySelector(`.slide-out-drawer#${this.drawerId}`);

    if (this.drawer) {
      this.handleDrawerClose = () => {
        this.button.setAttribute('aria-expanded', 'false');
      };
      this.handleDrawerOpen = () => {
        this.button.setAttribute('aria-expanded', 'true');
      };
      this.drawer.addEventListener('slide-out-drawer:close', this.handleDrawerClose);
      this.drawer.addEventListener('slide-out-drawer:open', this.handleDrawerOpen);
    } else {
      console.error(`Slide-out drawer opener: Drawer with id "${this.drawerId}" not found or is not a SlideOutDrawer instance`);
    }

    this.handleClick = drawerDebounce(() => {
      if (this.drawer && this.drawer instanceof SlideOutDrawer) {
        this.drawer.open();
      } else {
        console.error(`Slide-out drawer opener: Drawer with id "${this.drawerId}" not found or is not a SlideOutDrawer instance`);
      }
    }, 250);

    this.button.addEventListener('click', this.handleClick);
  }

  disconnectedCallback() {
    if (this.button && this.handleClick) {
      this.button.removeEventListener('click', this.handleClick);
    }

    if (this.drawer && this.handleDrawerClose) {
      this.drawer.removeEventListener('slide-out-drawer:close', this.handleDrawerClose);
    }

    if (this.drawer && this.handleDrawerOpen) {
      this.drawer.removeEventListener('slide-out-drawer:open', this.handleDrawerOpen);
    }
  }
}

if (!customElements.get('slide-out-drawer-opener')) {
  customElements.define('slide-out-drawer-opener', SlideOutDrawerOpener);
}
